import camelCase from 'camelcase';
import { EngagementType } from '../../../../types';
import { BlacklistEngagementType } from './billable-activities/billable-activities';

export interface SubTypeRules {
  defaultSelected: boolean;
  disabled: boolean;
  value: number;
}

export interface EngagementTypeWithRules extends EngagementType {
  defaultSelected: boolean;
  subTypeOptions: Record<RuleType, SubTypeRules[]> | null;
}

export enum RuleType {
  enso_session = 'dailyCap',
}

interface EngagementTypeConfig {
  hasSubTypes: boolean;
}

interface BlacklistItem {
  [key: string]: string | null;
}

const defaultSubTypeOptions = (): Record<RuleType, SubTypeRules[]> => ({
  [RuleType.enso_session]: [
    {
      defaultSelected: false,
      disabled: false,
      value: 1,
    },
    {
      defaultSelected: false,
      disabled: false,
      value: 2,
    },
    {
      defaultSelected: true,
      disabled: false,
      value: 3,
    },
  ],
});

export const getEngagementTypesConfig = (): Record<
  string,
  EngagementTypeConfig
> => ({
  enso_session: { hasSubTypes: true },
  et_session: { hasSubTypes: false },
  article_read: { hasSubTypes: false },
  video_visit: { hasSubTypes: false },
  in_person_visit: { hasSubTypes: false },
  care_team_communication: { hasSubTypes: false },
  orthopedic_specialist_video_visit: { hasSubTypes: false },
});

const getBlacklistItemsForEngagementType = (
  engagementType: string,
  blacklistEngagementTypes?: BlacklistEngagementType[],
): BlacklistItem[] =>
  blacklistEngagementTypes?.filter(
    item => Object.keys(item)[0] === engagementType,
  ) || [];

const isEngagementTypeAllowed = (
  engagementType: EngagementType,
  blacklistItems: BlacklistItem[],
): boolean => {
  if (blacklistItems.length === 0) return true;
  if (
    blacklistItems.some(item => item[camelCase(engagementType.name)] === null)
  )
    return false;

  if (engagementType.name === 'enso_session') {
    const hasBlacklistedDailyCap1 = blacklistItems.some(
      item => item[camelCase(engagementType.name)] === 'daily_cap_1',
    );
    const hasBlacklistedDailyCap3 = blacklistItems.some(
      item => item[camelCase(engagementType.name)] === 'daily_cap_3',
    );
    return !(hasBlacklistedDailyCap1 && hasBlacklistedDailyCap3);
  }

  return true;
};

const configureEnsoSessionSubTypes = (
  baseSubTypeOptions: Record<RuleType, SubTypeRules[]>,
  blacklistItems: BlacklistItem[],
  engagementType: string,
): Record<RuleType, SubTypeRules[]> => ({
  ...baseSubTypeOptions,
  [RuleType.enso_session]: baseSubTypeOptions[RuleType.enso_session].map(
    opt => {
      const isBlacklisted = blacklistItems.some(
        item => item[engagementType] === `daily_cap_${opt.value}`,
      );

      return {
        ...opt,
        disabled: isBlacklisted,
        defaultSelected: !isBlacklisted && opt.defaultSelected,
      };
    },
  ),
});

const getSubTypeOptions = (
  engagementType: EngagementType,
  engagementTypesConfig: Record<string, EngagementTypeConfig>,
  blacklistItems: BlacklistItem[],
): Record<RuleType, SubTypeRules[]> | null => {
  const baseSubTypeOptions = engagementTypesConfig[engagementType.name]
    ?.hasSubTypes
    ? defaultSubTypeOptions()
    : null;

  if (
    baseSubTypeOptions &&
    blacklistItems.length > 0 &&
    engagementType.name === 'enso_session'
  ) {
    return configureEnsoSessionSubTypes(
      baseSubTypeOptions,
      blacklistItems,
      camelCase(engagementType.name),
    );
  }

  return baseSubTypeOptions;
};

export const mapEngagementTypesToRules = (
  engagementTypes: EngagementType[],
  blacklistEngagementTypes?: BlacklistEngagementType[],
  defaultCheckedEngagementType?: string,
): EngagementTypeWithRules[] => {
  const engagementTypesConfig = getEngagementTypesConfig();

  return engagementTypes
    .filter(engagementType => {
      const blacklistItems = getBlacklistItemsForEngagementType(
        camelCase(engagementType.name),
        blacklistEngagementTypes,
      );
      return isEngagementTypeAllowed(engagementType, blacklistItems);
    })
    .map(engagementType => {
      const blacklistItems = getBlacklistItemsForEngagementType(
        camelCase(engagementType.name),
        blacklistEngagementTypes,
      );

      return {
        ...engagementType,
        defaultSelected: defaultCheckedEngagementType
          ? engagementType.name === defaultCheckedEngagementType
          : false,
        subTypeOptions: getSubTypeOptions(
          engagementType,
          engagementTypesConfig,
          blacklistItems,
        ),
      };
    });
};
